import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { CityPicker } from '@/components/CityPicker';
import { PageError } from '@/components/PageError';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface RateCard {
  id: string; code: string;
  origin_city: string; destination_city: string;
  vehicle_type: string; cargo_type: string | null;
  driver_rate_sar: string; client_rate_sar: string;
  effective_from: string | null; effective_to: string | null;
  status: string; notes: string | null;
}

const VEHICLE_TYPES = ['flatbed', 'water_tanker', 'sewage_tanker', 'container_truck', 'dumper', 'pickup', 'bus', 'other'];
const fmtSar = (s: string) => 'SAR ' + Number(s ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

export default function TripRateCardsPage() {
  const [items, setItems] = useState<RateCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RateCard | null>(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    code: '', originCity: '', destinationCity: '', vehicleType: 'flatbed',
    cargoType: '', driverRateSar: '', clientRateSar: '', notes: '',
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<RateCard[]>('/api/v1/trip-rate-cards?status=all');
      setItems(Array.isArray(r.data) ? r.data : []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function openEdit(rc: RateCard) {
    setEditing(rc);
    setForm({
      code: rc.code,
      originCity: rc.origin_city,
      destinationCity: rc.destination_city,
      vehicleType: rc.vehicle_type,
      cargoType: rc.cargo_type ?? '',
      driverRateSar: rc.driver_rate_sar,
      clientRateSar: rc.client_rate_sar,
      notes: rc.notes ?? '',
    });
  }
  function openCreate() {
    setCreating(true);
    setForm({ code: '', originCity: '', destinationCity: '', vehicleType: 'flatbed', cargoType: '', driverRateSar: '', clientRateSar: '', notes: '' });
  }
  function close() { setEditing(null); setCreating(false); }

  async function submit() {
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        originCity: form.originCity, destinationCity: form.destinationCity,
        vehicleType: form.vehicleType,
        cargoType: form.cargoType || null,
        driverRateSar: Number(form.driverRateSar), clientRateSar: Number(form.clientRateSar),
        notes: form.notes || null,
      };
      if (creating) {
        body.code = form.code;
        await apiClient.post('/api/v1/trip-rate-cards', body);
      } else if (editing) {
        await apiClient.patch('/api/v1/trip-rate-cards/' + editing.id, body);
      }
      close();
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setSubmitting(false); }
  }

  async function disable(rc: RateCard) {
    if (!window.confirm('Disable rate card ' + rc.code + '? Historic trips keep their rates.')) return;
    try { await apiClient.delete('/api/v1/trip-rate-cards/' + rc.id); await refresh(); }
    catch (e) { window.alert(extractErrorMessage(e)); }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium">Trip rate cards</div>
          <Button size="sm" onClick={openCreate} className="gap-1"><Plus className="h-4 w-4" /> Add rate</Button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-4 space-y-3">
        <p className="text-xs text-muted-foreground">
          Per-route pricing used to auto-fill driver pay + client rate when a flatbed trip is created.
          New routes append here automatically when admin types an unknown city.
        </p>

        {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : error ? <PageError error={error} retry={refresh} />
        : items.length === 0 ? <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No rate cards yet.</div>
        : <div className="rounded-lg border border-border overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Route</th>
                  <th className="text-left px-3 py-2">Vehicle</th>
                  <th className="text-left px-3 py-2">Cargo</th>
                  <th className="text-right px-3 py-2">Driver pay</th>
                  <th className="text-right px-3 py-2">Client rate</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((rc) => (
                  <tr key={rc.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 font-mono text-xs">{rc.code}</td>
                    <td className="px-3 py-2">{rc.origin_city} → {rc.destination_city}</td>
                    <td className="px-3 py-2 text-xs">{rc.vehicle_type.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-xs">{rc.cargo_type ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtSar(rc.driver_rate_sar)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtSar(rc.client_rate_sar)}</td>
                    <td className="px-3 py-2 text-xs">{rc.status}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(rc)}><Pencil className="h-3.5 w-3.5" /></Button>
                      {rc.status === 'active' && (
                        <Button size="sm" variant="ghost" onClick={() => disable(rc)}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </main>

      <Sheet open={creating || !!editing} onOpenChange={(o) => !o && close()}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{creating ? 'Add rate card' : 'Edit rate card'}</SheetTitle>
            <SheetDescription>{creating ? 'New per-route pricing' : 'Edit existing'}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
            {creating && (
              <div className="space-y-1">
                <Label>Code *</Label>
                <Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="RATE-JED-RYD-40FT" />
              </div>
            )}
            <div className="space-y-1">
              <Label>Origin *</Label>
              <CityPicker value={form.originCity} onChange={(v) => setForm({...form, originCity: v})} />
            </div>
            <div className="space-y-1">
              <Label>Destination *</Label>
              <CityPicker value={form.destinationCity} onChange={(v) => setForm({...form, destinationCity: v})} />
            </div>
            <div className="space-y-1">
              <Label>Vehicle type *</Label>
              <select value={form.vehicleType} onChange={(e) => setForm({...form, vehicleType: e.target.value})}
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                {VEHICLE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Cargo type (optional)</Label>
              <Input value={form.cargoType} onChange={(e) => setForm({...form, cargoType: e.target.value})} placeholder="40ft_container / 20ft_container / lowbed" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Driver pay (SAR) *</Label>
                <Input type="number" step="0.01" min={0} value={form.driverRateSar} onChange={(e) => setForm({...form, driverRateSar: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label>Client rate (SAR) *</Label>
                <Input type="number" step="0.01" min={0} value={form.clientRateSar} onChange={(e) => setForm({...form, clientRateSar: e.target.value})} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
            </div>
          </div>
          <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
            <Button variant="outline" onClick={close} disabled={submitting}>Cancel</Button>
            <Button onClick={submit} disabled={submitting || !form.originCity || !form.destinationCity || !form.driverRateSar || !form.clientRateSar || (creating && !form.code)}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
